import React, { useRef, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import { MagnifyingGlassIcon, ChevronRightIcon } from "@heroicons/react/24/outline"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { getHelpCenterContext, DEFAULT_CONTEXT } from "../context"

const Search = ({ onResultSelect, searchState }) => {
  const texts = TextBundle({ id: "help-center" })
  const {
    searchTerm,
    searchResults,
    isSearching,
    showResults,
    handleSearchChange,
    resetSearch,
    closeResults,
    setShowResults,
  } = searchState

  const searchRef = useRef(null)

  const handleClickOutside = (e) => {
    if (searchRef.current && !searchRef.current.contains(e.target)) closeResults()
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleFocus = () => {
    if (!searchTerm) return
    if (searchResults.length > 0) {
      setShowResults(true)
    } else {
      // Re-trigger search if there's a term but no cached results
      const { performSearch } = searchState
      if (performSearch) performSearch(searchTerm)
      setShowResults(true)
    }
  }

  const handleResultClick = (result) => {
    closeResults()
    resetSearch()
    if (onResultSelect) onResultSelect(result)
  }

  return (
    <div
      ref={searchRef}
      className="search-container relative w-full max-w-[604px] lg:max-w-[674px] flex flex-col items-center mx-auto"
      style={{ position: "relative", zIndex: 100 }}
    >
      <div className="relative w-full flex items-center" onFocus={handleFocus}>
        <Input
          type="text"
          variant="hc_search"
          // the context's own words when the bundle has them (search_placeholder_vendor)
          placeholder={(getHelpCenterContext() !== DEFAULT_CONTEXT && texts?.[`search_placeholder_${getHelpCenterContext()}`]) || texts?.search_placeholder || "Search articles"}
          className="w-full h-[56px] md:h-[66px] rounded-full text-xl"
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <button
          className="absolute w-[46px] md:w-14 h-[46px] md:h-14 right-[6px] flex items-center justify-center bg-primary hover:bg-primary_hov text-white rounded-full"
          aria-label={texts?.search_aria || "Search"}
        >
          <MagnifyingGlassIcon className="w-[30px] h-[30px] text-white" />
        </button>
      </div>

      {showResults && searchResults.length > 0 && (
        <div
          className="absolute w-full bg-paper rounded-[20px] shadow-md p-2 max-h-[60vh] overflow-y-auto"
          style={{
            zIndex: 100,
            position: "absolute",
            top: "100%",
            marginTop: "8px",
            filter: "drop-shadow(0 4px 16px rgba(0, 0, 0, 0.1))",
          }}
        >
          {searchResults.map((result, index) => (
            <div
              key={`${result.type}-${result.item.slug || index}`}
              className={`cursor-pointer group ${index > 0 ? "border-t border-bordered" : ""}`}
              onClick={() => handleResultClick(result)}
            >
              <div className="px-4 py-5 mx-1 rounded-[16px] group-hover:bg-neutral_1 transition-colors">
                <Typography variant="body1" className="text-text mt-0 mb-2 text-left font-medium">
                  {result.item.title}
                </Typography>

                <div className="flex items-center flex-wrap gap-1">
                  <Typography variant="breadcrumbs" className="!text-text_secondary my-0">
                    {texts?.help_center || "Help Center"}
                  </Typography>

                  {result.displayPath && result.displayPath.length > 0 && (
                    <>
                      <ChevronRightIcon className="w-3.5 h-3.5 text-text_secondary" />
                      {result.displayPath.map((pathItem, i) => (
                        <React.Fragment key={`path-${i}`}>
                          <Typography variant="breadcrumbs" className="!text-text_secondary my-0">
                            {pathItem}
                          </Typography>
                          {i < result.displayPath.length - 1 && (
                            <ChevronRightIcon className="w-3.5 h-3.5 text-text_secondary" />
                          )}
                        </React.Fragment>
                      ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showResults && searchTerm && searchResults.length === 0 && !isSearching && (
        <div
          className="absolute w-full bg-paper rounded-[20px] shadow-md overflow-hidden p-6 text-center"
          style={{
            zIndex: 100,
            position: "absolute",
            top: "100%",
            marginTop: "8px",
            filter: "drop-shadow(0 4px 16px rgba(0, 0, 0, 0.1))",
          }}
        >
          <Typography variant="body1_dim">
            {texts?.no_search_results || "No results found"}
          </Typography>
        </div>
      )}
    </div>
  )
}

export default Search
